// Who opened the routing pages (`app-user`): the person's own record — their sign-in email, name, first
// and last seen — written by their browser, at most every 10 minutes, so Admin → Customer assignments →
// Add can suggest them. Never in the System log (`{ own }`); only their own record (a `beforeWrite` hook).
//
// OUTSIDE-PLATFORM: P17 the server cannot create a record by itself, and P20 the platform has no user
// directory (docs/platform-requests.md).

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { APP_USERS, emailKey } from "@app/shared";
import { crudApi, useMe } from "@axny/platform-ui";
import { MY_VIEW_KEY, useMyView } from "./data";
import { useLoggedWrites } from "./logged-write";
import { MessageProvider } from "./message";

/** A person's record is written again only this long after its last seen. */
export const SEEN_EVERY_MS = 10 * 60_000;

/** Whether a record last seen at `lastSeen` is written again at `now`. */
export const seenIsDue = (lastSeen: string | null | undefined, now: number): boolean => {
  const at = lastSeen ? Date.parse(lastSeen) : NaN;
  return Number.isNaN(at) || now - at >= SEEN_EVERY_MS;
};

/** Mounted once, by the shell, on every routing page. Renders nothing. */
export function AppUserSeen() {
  return (
    <MessageProvider>
      <Seen />
    </MessageProvider>
  );
}

function Seen() {
  const me = useMe();
  const my = useMyView();
  const writes = useLoggedWrites();
  const queryClient = useQueryClient();
  const running = useRef(false);
  /** This page's last try: no second write before the record read back shows it (nor after a refusal). */
  const triedAt = useRef(0);
  const user = me.data?.authenticated ? me.data.user : null;

  useEffect(() => {
    const email = emailKey(user?.username);
    const now0 = Date.now();
    if (!email || !my.data || running.current) return;
    if (!seenIsDue(my.data.appUser?.lastSeen, now0) || now0 - triedAt.current < SEEN_EVERY_MS) return;
    running.current = true;
    triedAt.current = now0;
    const now = new Date().toISOString();
    const mine = my.data.appUser;
    const input = { email, displayName: user?.displayName || email, lastSeen: now };
    const log = { own: "who opened the app" };
    void (async () => {
      try {
        if (mine) {
          // Its version now: another tab of the person's may have written it since.
          const current = await crudApi.get(APP_USERS.id, mine.id);
          await writes.update(APP_USERS, mine.id, String(current.rv), { ...input, firstSeen: mine.firstSeen }, log);
        } else await writes.create(APP_USERS, { ...input, firstSeen: now }, log);
        await queryClient.invalidateQueries({ queryKey: MY_VIEW_KEY });
      } catch {
        // Another tab wrote it first (409, or the one-per-person index): nothing to do; it is tried again
        // on the next routing page after 10 minutes.
      } finally {
        running.current = false;
      }
    })();
  }, [user?.username, user?.displayName, my.data, writes, queryClient]);
  return null;
}
